import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useErrorInfoModal } from '@features/modal'

interface SuggestOption {
  title: { text: string };
  subtitle?: { text: string };
  tags?: string[];
  distance?: { value: number; text: string };
}

interface UseYandexSuggestOptions {
  apiKey: string;
  lang?: 'ru_RU' | 'kk_KZ';
  coordinates?: [number, number];
  autoFetch?: boolean;
  bbox?: string; // format: "minLng,minLat,maxLng,maxLat"
}

export const useYandexSuggest = ({
  apiKey,
  lang = 'ru_RU',
  coordinates,
  autoFetch = true,
  bbox,
}: UseYandexSuggestOptions) => {
  const [ query, setQuery ] = useState('')
  const [ suggestions, setSuggestions ] = useState<SuggestOption[]>([])
  const [ loading, setLoading ] = useState(false)
  const [ skipNextFetch, setSkipNextFetch ] = useState(false)
  const { t: te } = useTranslation('errors')
  const { openErrorInfoModal } = useErrorInfoModal()

  const sessionTokenRef = useRef<string>(crypto.randomUUID())

  const fetchSuggestions = async (text: string) => {
    if (!text.trim()) {
      setSuggestions([])
      return
    }

    setLoading(true)

    try {
      const params = new URLSearchParams({
        text,
        lang,
        sessionToken: sessionTokenRef.current,
        apikey: apiKey,
        results: '10',
        types: 'street,district,locality,house,province',
      })

      if (coordinates) {
        params.append('ull', `${coordinates[0]},${coordinates[1]}`)
      }

      // Ограничение области поиска (bounding box)
      if (bbox) {
        params.append('bbox', bbox)
      }

      const res = await fetch(
        `https://suggest-maps.yandex.ru/v1/suggest?${params.toString()}`,
      )

      const data = await res.json()
      setSuggestions(data.results || [])
    }
    catch (error) {
      console.error('Failed to fetch suggestions', error)
      openErrorInfoModal({
        description: te('fetch_suggestions'),
      })
    }
    finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!autoFetch) return
    
    // Пропускаем запрос если установлен флаг (для предзаполнения без подсказок)
    if (skipNextFetch) {
      setSkipNextFetch(false)
      return
    }

    const controller = new AbortController()

    fetchSuggestions(query)

    return () => controller.abort()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ query, lang, apiKey, coordinates, autoFetch ])

  const clearSuggestions = () => {
    setSuggestions([])
  }
  
  // Установить query без триггера запроса подсказок
  const setQueryWithoutFetch = (value: string) => {
    setSkipNextFetch(true)
    setSuggestions([])
    setQuery(value)
  }

  return {
    query,
    setQuery,
    setQueryWithoutFetch,
    suggestions,
    loading,
    clearSuggestions,
    fetchSuggestions,
  }
}
